import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Modal, ModalDialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/joy';
import PropTypes from 'prop-types';

import noteActions from 'actions/noteActions';
import lookupTableActions from 'actions/lookupTableActions';

import LookupTableForm from './LookupTableForm';
import EventForm from './EventForm';
import LocationForm from './LocationForm';
import PersonForm from './PersonForm';
import AddUserForm from './AddUserForm';

import { lookupTableClasses } from 'utils/LookupTableUtils';

const CreateModal = ({ open, onClose, className }) => {
    const dispatch = useDispatch();
    const options = useSelector(({ lookupTable }) => lookupTable.options);

    useEffect(() => {
        if (open) {
            dispatch(lookupTableActions.fetchAllOptions());
        }
    }, [open, dispatch]);

    const handleSubmit = async (savedRecord) => {
        onClose();
    };

    const handleLocationSubmit = async (location, notes, noteDiff) => {
        try {
            if (notes && notes.length > 0) {
                await Promise.all(notes.map(note => {
                    if (note.type && note.text.trim()) {
                        return dispatch(noteActions.save({
                            location: location.id,
                            type: note.type.id || note.type,
                            text: note.text,
                            parent: location.id
                        }));
                    }
                    return Promise.resolve();
                }));
            }
            onClose();
        } catch (err) {
            console.error('Failed to save location notes:', err);
            onClose();
        }
    };

    const getFormComponent = () => {
        const isLookupTable = lookupTableClasses.some(ltc => ltc.className === className);
        
        const formProps = {
            onSubmit: handleSubmit
        };

        if (isLookupTable || className === 'Subject') {
            return <LookupTableForm {...formProps} className={className} />;
        }

        switch (className) {
            case 'Event':
                return <EventForm {...formProps} />;
            case 'Location':
                const locationNoteTypes = options.noteTypes
                    ?.filter(noteType => noteType.get('filteredFor') === 'Location') || [];
                
                const defaultLocationNotes = locationNoteTypes.map(noteType => ({
                    type: noteType,
                    text: '',
                    isDefault: true
                }));
                
                return <LocationForm 
                    {...formProps} 
                    notes={defaultLocationNotes}
                    onSubmit={handleLocationSubmit}
                />;
            case 'Person':
                return <PersonForm {...formProps} />;
            case 'User':
                return <AddUserForm />;
            default:
                return <div>No form available for {className}</div>;
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <ModalDialog variant="outlined" size="lg" sx={{ maxWidth: '800px', maxHeight: '90vh', overflow: 'auto' }}>
                <DialogTitle>Create New {className}</DialogTitle>
                <DialogContent>
                    {getFormComponent()}
                </DialogContent>
                <DialogActions>
                    <Button variant="plain" color="neutral" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    );
};

CreateModal.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    className: PropTypes.string
};

export default CreateModal;